// libraries/components/src/components/upcoming-trips/services/itinerary-actions.ts

import type {
  ItineraryContent,
  ItineraryServiceResponse,
} from "@jb-manage-trips/types/itinerary";

/* ---------- Language helpers (port of LanguageService) ---------- */

const LANG_MAPPINGS = {
  en: { code: "en-US", lang: "en" },
  es: { code: "es-ES", lang: "es" },
  fr: { code: "fr-FR", lang: "fr" },
  // extend as needed
};

export type SupportedLang = keyof typeof LANG_MAPPINGS;

type LegacyNavigator = Navigator & {
  opera?: string;
  vendor?: string;
};

type WindowWithLegacyMsStream = Window & {
  MSStream?: unknown;
};

function isSupportedLang(value: string): value is SupportedLang {
  return value in LANG_MAPPINGS;
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  const match = document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`));

  if (!match) {
    return undefined;
  }

  const value = match.split("=")[1];

  return value ? decodeURIComponent(value) : undefined;
}

/**
 * Equivalent to LanguageService.getLanguage()
 */
function getLanguage(options: {
  useLanguageInUrl: boolean;
  supportedLanguages: string[];
}): SupportedLang {
  if (!("window" in globalThis)) {
    return "en";
  }

  if (options.useLanguageInUrl) {
    const pathname = globalThis.window.location.pathname;
    const firstSegment = pathname.split("/")[1];
    const match = options.supportedLanguages.find((l) => l === firstSegment);

    if (match && isSupportedLang(match)) {
      return match;
    }

    return "en";
  }

  const cookieLang = readCookie("lang");

  if (cookieLang && isSupportedLang(cookieLang)) {
    return cookieLang;
  }

  return "en";
}

/**
 * Equivalent to LanguageService.getLocale()
 */
function getLocaleFromLang(lang: SupportedLang): string {
  return LANG_MAPPINGS[lang].code;
}

/* ---------- Platform + navigation helpers (port of WindowService) ---------- */

type MobileOS = "Android" | "Other" | "iOS";

function checkMobileOperatingSystem(): MobileOS {
  if (!("window" in globalThis)) {
    return "Other";
  }

  const navigator = globalThis.window.navigator as LegacyNavigator;
  const windowObject = globalThis.window as WindowWithLegacyMsStream;
  const ua = navigator.userAgent;

  if (/iPad|iPhone|iPod/.test(ua) && !windowObject.MSStream) {
    return "iOS";
  }

  if (/android/i.test(ua)) {
    return "Android";
  }

  return "Other";
}

function navigateOutsideUrl(url: string): void {
  if (!("window" in globalThis)) {
    return;
  }

  globalThis.window.location.assign(url);
}

/* ---------- Cookie validation (port of validateCookies) ---------- */

function deleteCookie(name: string): void {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${name}=; Max-Age=0; path=/`;
}

/**
 * Port of ItineraryComponent.validateCookies().
 *
 * In JS, expired cookies simply don't appear in document.cookie,
 * so we treat "missing" as invalid and delete them to be safe.
 */
function validateCookies(cookieNames: string[]): boolean {
  if (typeof document === "undefined") {
    return true; // server-side no-op
  }

  let allCookiesValid = true;

  for (const cookieName of cookieNames) {
    const hasCookie = document.cookie
      .split("; ")
      .some((c) => c.startsWith(`${cookieName}=`));

    if (!hasCookie) {
      allCookiesValid = false;
      deleteCookie(cookieName);
    }
  }

  return allCookiesValid;
}

/* ---------- Public itinerary action API ---------- */

export type SabreDxConfig = {
  sabreDxBaseUrl: string; // from app-config (sabreDXUrl)
  supportedLanguages: string[];
  useLanguageInUrl: boolean;
};
/**
 * Port of ManageFlightsService._generateSabreDxUrl()
 */
export function generateSabreDxUrl(
  lastName: string,
  pnrRecordLocator: string,
  queryParameters: URLSearchParams,
  config: SabreDxConfig,
): string {
  const urlLocale = queryParameters.get("locale");
  const lang = getLanguage({
    supportedLanguages: config.supportedLanguages,
    useLanguageInUrl: config.useLanguageInUrl,
  });
  const locale = urlLocale ?? getLocaleFromLang(lang);
  const platform = queryParameters.get("un_jtt_application_platform");

  let source: string | undefined;

  if (platform) {
    const os = checkMobileOperatingSystem();

    if (os === "iOS") {
      source = "imobile";
    } else if (os === "Android") {
      source = "amobile";
    }
  }

  let redirectUrl = `${config.sabreDxBaseUrl}?lastName=${encodeURIComponent(
    lastName,
  )}&pnr=${encodeURIComponent(pnrRecordLocator)}&locale=${encodeURIComponent(
    locale,
  )}`;

  if (source) {
    redirectUrl += `&source=${encodeURIComponent(source)}`;
  }

  return redirectUrl;
}
/**
 * Port of ItineraryComponent._getSelfServiceUrl()
 */
export function getSelfServiceUrl(
  itineraryItem: ItineraryServiceResponse,
  options: {
    supportedLanguages: string[];
    useLanguageInUrl: boolean;
  },
): string | null {
  const lang = getLanguage(options);
  const urls = itineraryItem.selfService?.urls;
  const rawUrl = urls?.[0]?.url;

  if (!rawUrl) {
    return null;
  }

  const [selfServiceUrl = "", fragment = ""] = rawUrl.split("#");
  const parameters = new URLSearchParams(fragment);

  parameters.delete("lang");
  parameters.set("lang", lang);

  const updatedFragment = parameters.toString();

  return `${selfServiceUrl}#${updatedFragment}`;
}
/**
 * Port of ItineraryComponent.setAriaLabelButton()
 */
export function setAriaLabelButton(
  content: ItineraryContent,
  origin: string,
  destination: string,
  type: "disrupted" | "normal",
): string {
  return content.submit[type].ariaLabel
    .replace("[ORIGIN]", origin)
    .replace("[DESTINATION]", destination);
}
/**
 * Port of ItineraryComponent.getFlightRoute()
 */
export function getFlightRoute(
  content: ItineraryContent,
  origin: string,
  destination: string,
): string {
  return content.flightRoute
    .replace("[ORIGIN]", origin)
    .replace("[DESTINATION]", destination);
}
export type ItineraryButtonHandlerConfig = {
  cookieNamesToValidate?: string[];
} & SabreDxConfig;
/**
 * Port of ItineraryComponent.onButtonClick(), adapted for React/Next.
 *
 * - Validates cookies
 * - If self-service button: navigates to self-service URL
 * - Else: constructs Sabre DX URL and navigates
 */
export function handleItineraryButtonClick(
  itineraryItem: ItineraryServiceResponse,
  isSelfServiceButton: boolean,
  queryParameters: URLSearchParams,
  config: ItineraryButtonHandlerConfig,
): void {
  const cookieNames = config.cookieNamesToValidate ?? [
    "jsessionid",
    "dcsessionid",
    "cid",
  ];

  validateCookies(cookieNames);

  if (isSelfServiceButton) {
    const url = getSelfServiceUrl(itineraryItem, {
      supportedLanguages: config.supportedLanguages,
      useLanguageInUrl: config.useLanguageInUrl,
    });

    if (url) {
      navigateOutsideUrl(url);
    }

    return;
  }

  const lastName = itineraryItem.lastName ?? "";
  const pnr =
    itineraryItem.pnrRecordLocator ?? itineraryItem.b6PNRRecordLocator ?? "";

  if (!lastName || !pnr) {
    return;
  }

  const sabreUrl = generateSabreDxUrl(lastName, pnr, queryParameters, config);
  navigateOutsideUrl(sabreUrl);
}
